import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, importProvidersFrom } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';

import { BrowserModule } from '@angular/platform-browser';

import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { NgbAlertModule, NgbModule } from '@ng-bootstrap/ng-bootstrap';

import { OKTA_CONFIG, OktaAuthModule } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { ErrorIntercept } from '../interceptors/error.interceptor';
import { InterceptorDateHttp } from '../interceptors/interceptorDateHttp';
import { LoaderInterceptor } from '../interceptors/interceptorLoader';


import { NotificationService } from '@progress/kendo-angular-notification';

import { routes } from "./app.routes";

import { provideZoneChangeDetection } from "@angular/core";

import {
    HOT_GLOBAL_CONFIG,
    HotGlobalConfig
} from "@handsontable/angular-wrapper";
import { HotTableModule } from '@handsontable/angular-wrapper';
import { registerLanguageDictionary, enUS } from 'handsontable/i18n';

registerLanguageDictionary(enUS);

const globalHotConfig: HotGlobalConfig = {
  license: "d7a13-75e59-83d71-04323-8183a",
  layoutDirection: "ltr",
  language: "en-US",
  themeName: "ht-theme-main",
};

import { LicenseServiceGrid, RoleTemplateServiceGrid } from './features/admin';

import {
    ApplicationInsightsErrorHandler,
    ConfirmationDialogHtmlPaneService,
    ConfirmationDialogService,
    UiWindowService,
    UserService
} from './modules/fwcore';

import { initSettingsTabs } from './modules/fwcore/components/user-settings/user-settings.func';
import { FwcoreModule } from './modules/fwcore/fwcore.module';

import { provideLottieOptions } from 'ngx-lottie';
import player from 'lottie-web';

export const appConfig = (appSettings: any): ApplicationConfig => ({

  providers: [

    // imported module providers
    importProvidersFrom(
      BrowserModule
      , OktaAuthModule
      , NgbModule
      , NgbAlertModule
      , FwcoreModule
      , NgIdleKeepaliveModule.forRoot()
      , HotTableModule
    )

    // base providers
    , { provide: 'BASE_URL', useFactory: () => document.getElementsByTagName('base')[0].href }
    , { provide: 'CONFIG_SETTINGS', useValue: appSettings }

    // OKTA configuration
    , {
      provide: OKTA_CONFIG
      , useFactory: (config: any) => {
        const oktaSettings = config['okta'];

        return {
          oktaAuth: new OktaAuth({
            issuer: oktaSettings.issuer
            , redirectUri: oktaSettings.redirectUri
            , postLogoutRedirectUri: oktaSettings.postLogoutRedirectUri
            , clientId: oktaSettings.clientId
            , scopes: Array.from(oktaSettings.scopes)
            , pkce: JSON.parse(oktaSettings.pkce)
            , tokenManager: {
              storage: 'localStorage'
              , autoRenew: true
              , secure: true
              , expireEarlySeconds: 300
            }
            , services: {
              autoRenew: true
              , autoRemove: true
            }
          })
        };
      }
      , deps: ['CONFIG_SETTINGS']
    }

    // HTTP interceptors
    , { provide: HTTP_INTERCEPTORS, useClass: InterceptorDateHttp, multi: true }
    , { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true }
    , { provide: HTTP_INTERCEPTORS, useClass: ErrorIntercept, multi: true }

    // App initializer
    , {
      provide: APP_INITIALIZER
      , useFactory: initSettingsTabs
      , deps: [UserService]
      , multi: true
    }

    // global error handler
    , { provide: ErrorHandler, useClass: ApplicationInsightsErrorHandler }

    // standalone providers
    , provideHttpClient(withInterceptorsFromDi())
    , provideAnimations()
    , provideZoneChangeDetection({ eventCoalescing: true })

    // shared services
    , UiWindowService
    , NotificationService
    , ConfirmationDialogService
    , ConfirmationDialogHtmlPaneService
    , LicenseServiceGrid
    , RoleTemplateServiceGrid

    // router
    , provideRouter(routes)

    // handsontable global config
    , { provide: HOT_GLOBAL_CONFIG, useValue: globalHotConfig }

    // lottie animation player
    , provideLottieOptions({ player: () => player })
  ]
});
